import { createFileRoute } from "@tanstack/react-router";
import { Clause, LegalLayout } from "@/components/sox/LegalLayout";
import { LEGAL } from "@/lib/legal";
import { openCookiePreferences } from "@/components/sox/CookieConsent";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { title: "Cookie Policy — SOXIE365" },
      {
        name: "description",
        content:
          "Which cookies SOXIE365 uses, what each one does, how long it lasts, and how to change your consent at any time.",
      },
      { property: "og:title", content: "Cookie Policy — SOXIE365" },
      {
        property: "og:description",
        content: "Full cookie inventory for SOXIE365 and how to manage your consent.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://www.soxie365.com/cookies" },
    ],
    links: [{ rel: "canonical", href: "https://www.soxie365.com/cookies" }],
  }),
  component: CookiePage,
});

const ROWS = [
  {
    name: "soxie365_consent",
    provider: "SOXIE365",
    purpose: "Stores your cookie choices so we don't ask again.",
    type: "Strictly necessary",
    duration: "6 months",
  },
  {
    name: "soxie365_cart_v2",
    provider: "SOXIE365",
    purpose: "Keeps the item in your cart between page loads (local storage).",
    type: "Strictly necessary",
    duration: "Until cleared",
  },
  {
    name: "__stripe_mid / __stripe_sid",
    provider: "Stripe",
    purpose: "Fraud prevention and secure card processing during checkout.",
    type: "Strictly necessary",
    duration: "30 minutes – 1 year",
  },
  {
    name: "_ga / _ga_*",
    provider: "Google Analytics",
    purpose: "Measures visits and how pages are used, in aggregate.",
    type: "Analytics (consent required)",
    duration: "up to 14 months",
  },
];

function CookiePage() {
  return (
    <LegalLayout
      index="02"
      title="Cookie Policy"
      intro="We keep cookies to the minimum. Strictly necessary cookies run the shop and checkout. Analytics cookies only load after you accept them — and never before."
    >
      <Clause heading="1. How consent works here">
        <p>
          On your first visit, no analytics cookies are set. Google Consent Mode starts in a fully
          denied state, so nothing is measured until you press "Accept". Rejecting is one click and
          the site works exactly the same. Your choice is stored for 6 months and you can change it
          whenever you like.
        </p>
        <p>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="border-2 border-black bg-black px-5 py-2 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-accent hover:text-black"
          >
            Change cookie settings
          </button>
        </p>
      </Clause>

      <Clause heading="2. Cookies we use">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b-2 border-black font-mono text-xs uppercase tracking-widest">
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Provider</th>
                <th className="py-3 pr-4">Purpose</th>
                <th className="py-3 pr-4">Category</th>
                <th className="py-3">Duration</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.name} className="border-b border-black/15 align-top">
                  <td className="py-3 pr-4 font-mono text-xs">{r.name}</td>
                  <td className="py-3 pr-4">{r.provider}</td>
                  <td className="py-3 pr-4">{r.purpose}</td>
                  <td className="py-3 pr-4">{r.type}</td>
                  <td className="py-3">{r.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Clause>

      <Clause heading="3. Browser controls">
        <p>
          You can also block or delete cookies in your browser settings. Blocking strictly necessary
          cookies may break the cart and checkout. Browsers sending a Global Privacy Control signal
          are treated as an opt-out of analytics.
        </p>
      </Clause>

      <Clause heading="4. Questions">
        <p>
          Email <a href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a> and we'll explain
          anything on this page in plain language.
        </p>
      </Clause>
    </LegalLayout>
  );
}
